import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Lock, User as UserIcon, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useUserAuth } from '../contexts/UserAuthContext';
import { isNativeApp } from '../lib/nativeBridge';
import { getRecentEmails } from '../lib/nativeSession';
// iter304 · Wrapper for the Google/Apple sign-in buttons that catches
// any error thrown by the async provider flow (missing native plugin,
// SFSafariViewController failing to present, offline, etc.) and
// surfaces it as a toast + inline error so the user gets feedback
// instead of a dead button that "doesn't take them anywhere".
const runProviderLogin = async (fn, setError) => {
  try {
    await fn();
  } catch (err) {
    const msg = err?.message || 'Could not open the sign-in browser. Please try again.';
    try { toast.error(msg); } catch { /* toast might be unavailable */ }
    if (typeof setError === 'function') setError(msg);
  }
};
const GoogleGlyph = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16.1 19 13 24 13c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.5 6.3 14.7z"/>
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.2c-2 1.4-4.5 2.4-7.3 2.4-5.2 0-9.7-3.3-11.3-7.9l-6.5 5C9.4 39.4 16.1 44 24 44z"/>
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.3 5.6l6.2 5.2C40.8 35.5 44 30.3 44 24c0-1.3-.1-2.4-.4-3.5z"/>
  </svg>
);
const FieldRow = ({ icon: Icon, label, ...props }) => (
  <label className="block">
    {label && (
      <span
        className="block text-[10px] uppercase tracking-[0.28em] text-blue-300 font-bold mb-1.5"
      >
        {label}
      </span>
    )}
    <div className="relative">
      <Icon className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-white/60 pointer-events-none z-10" />
      <input
        {...props}
        className="w-full rounded-xl text-sm placeholder-white/60 focus:outline-none focus:border-blue-400"
        style={{
          // Inline overrides that beat the global `input { background:#fff }`
          // rule in App.css. Border + radius stay in Tailwind because the
          // cascade lets those through.
          //
          // iter288 · Padding pinned inline too. The global
          // `input[type="text"] { padding: 14px 18px }` in App.css (attr
          // selector, specificity 0,1,1) was beating Tailwind's `.pl-10`
          // (class, specificity 0,1,0) and pushing text under the leading
          // icons — exactly the "Jordan Smith" / "you@example.com" overlap
          // Jay caught on signup. Inline styles win the cascade so the
          // icon gutter stays clear at every viewport.
          padding: '14px 14px 14px 42px',
          backgroundColor: 'rgba(15,15,20,0.85)',
          color: '#ffffff',
          border: '1px solid rgba(255,255,255,0.20)',
          // iter421 · WKWebView on iOS gives `<input type="date">` an
          // intrinsic width based on the locale's placeholder text
          // (e.g. "mm/dd/yyyy"). That width IGNORED our `w-full` and
          // spilled the DOB field past the modal's right edge on 390pt
          // iPhones. Forcing box-sizing + max/min-width + stripping the
          // WebKit chrome pins the input inside its parent on every
          // device, and doesn't change the look of `type="text"` fields
          // one bit.
          boxSizing: 'border-box',
          maxWidth: '100%',
          minWidth: 0,
          WebkitAppearance: 'none',
          appearance: 'none',
          // Kill Chrome/Safari autofill white-background override.
          WebkitBoxShadow: '0 0 0 1000px rgba(15,15,20,0.98) inset',
          WebkitTextFillColor: '#ffffff',
          caretColor: '#ffffff',
        }}
      />
    </div>
  </label>
);
const LoginModal = () => {
  const { loginOpen, setLoginOpen, loginMode, setLoginMode, startGoogleLogin, emailLogin, emailSignup, startAppleLogin, verifyEmail, resendVerification } = useUserAuth();
  const [mode, setMode] = useState('login'); // login | signup | forgot | forgot_sent | verify
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '', remember: true, refCode: '' });
  // iter447 · Apple Guideline 1.2 (UGC Safety) — EULA acceptance is
  // MANDATORY before an account can be created. The reviewer must see
  // this checkbox on the signup panel BEFORE the form submits. Once
  // ticked, we stamp a local flag so returning users don't get re-
  // prompted every session.
  const [agreedEula, setAgreedEula] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  // iter673 · Email-verification step state.
  const [code, setCode] = useState('');
  const [pendingEmail, setPendingEmail] = useState('');
  // When the backend reports the account exists via Google (409
  // `no_password_set`), we show a targeted inline prompt to Continue
  // with Google or use Forgot password — instead of the generic error.
  const [noPasswordMode, setNoPasswordMode] = useState(false);
  // iter377 · Recent-email suggestions (MRU, up to 3). Populated on
  // modal open via getRecentEmails(). Tapping a chip fills the email
  // input which triggers iOS Passwords / Face ID autofill for the
  // saved credential.
  const [recentEmails, setRecentEmails] = useState([]);
  // Native shell: only offer Apple when the native plugin is actually
  // compiled into this binary. Older TestFlight builds receive this JS
  // via live update BEFORE they ship the plugin — hide the button there
  // instead of erroring on tap.
  const appleAvailable = !isNativeApp()
    || (typeof window !== 'undefined' && window.Capacitor?.isPluginAvailable?.('SignInWithApple'));
  useEffect(() => {
    if (!loginOpen) { setLoginMode('login'); return; }
    setMode(loginMode || 'login');
    // iter304 · Pre-fill the referral field with any code already
    // stashed from a `?ref=CODE` visit or the /r/:code redirect, so
    // the user sees "credit was captured".
    let stashed = '';
    try {
      stashed = (localStorage.getItem('flyt_ref_code') || '').toUpperCase();
    } catch (_) { /* private mode */ }
    // iter459e · Prefill the email if the caller stashed one on
    // sessionStorage (e.g. the "Log In Now" button on the claim-
    // submitted queue screen — the athlete just typed this email
    // three steps ago, so autopopulating saves them re-typing on
    // native iOS). One-shot: we clear it after read.
    let prefillEmail = '';
    try {
      prefillEmail = window.sessionStorage.getItem('flyt:prefill_email') || '';
      if (prefillEmail) window.sessionStorage.removeItem('flyt:prefill_email');
    } catch (_) { /* private mode */ }
    setForm({ name: '', email: prefillEmail, password: '', confirm: '', remember: true, refCode: stashed });
    setError(''); setNotice(''); setBusy(false); setNoPasswordMode(false);
    setCode(''); setPendingEmail('');
    // iter377 · Populate recent-email chips whenever the modal opens
    // in sign-in mode so returning users get a one-tap way to pick
    // their account. iOS Passwords autofill then offers Face ID for
    // the matching credential once the email is filled.
    getRecentEmails().then((list) => { setRecentEmails(list || []); }).catch(() => setRecentEmails([]));
    const onKey = (e) => { if (e.key === 'Escape') setLoginOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [loginOpen, loginMode, setLoginOpen, setLoginMode]);
  if (!loginOpen) return null;
  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  // iter863 · Jay: DOB is no longer collected on this form at all —
  // every signup method (Google, Apple, email alike) now gets asked
  // exactly once by the universal post-login DobGuard, so age gating
  // doesn't live here anymore.
  const submit = async (e) => {
    e.preventDefault();
    setError(''); setNotice(''); setNoPasswordMode(false);
    if (mode === 'signup') {
      if (!form.name.trim()) { setError('Please enter your name.'); return; }
      if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
      if (form.password !== form.confirm) { setError('Passwords don\'t match.'); return; }
      // iter447 · UGC Safety — enforce EULA acceptance server-blocker.
      // iter754 · This used to be backed up by the submit button itself
      // being natively `disabled` until the checkbox was ticked — but a
      // disabled button never fires a click/submit at all, so this guard
      // never actually RAN for the exact case it exists to catch (Jay:
      // tapping the greyed-out button did nothing, with zero explanation
      // of why). The button is no longer natively disabled for this
      // reason alone (see below) — it now reaches this check and gets a
      // real, visible answer.
      if (!agreedEula) {
        toast.error("Please agree to Flythouse's Terms of Service to continue.");
        setError('Please agree to Flythouse\'s Terms of Service to continue.');
        return;
      }
    }
    setBusy(true);
    try {
      if (mode === 'signup') {
        const res = await emailSignup({
          name: form.name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          refCode: form.refCode.trim().toUpperCase() || null,
          // iter863 · DOB moved entirely to the universal post-login
          // DobGuard — not collected on this form anymore.
          dob: null,
          // Guardian info isn't collected at signup — see the note
          // above submit(). GuardianConsentBanner handles it later,
          // only when the athlete tries to do something that needs it.
          guardianName: null,
          guardianEmail: null,
          guardianRelationship: null,
          // iter588 · Terms of Service consent receipt — captured here
          // and persisted on the public_users doc so we have an audit
          // trail if compliance ever asks.
          termsAccepted: agreedEula,
          termsVersion: 'v1',
        });
        // iter673 · New email accounts come back PENDING — move to the
        // 6-digit code step instead of closing/logging in.
        if (res?.pending_verification) {
          const em = res.email || form.email.trim().toLowerCase();
          setPendingEmail(em);
          setError('');
          setNotice(`We sent a 6-digit code to ${em}. Enter it below to activate your account.`);
          setCode('');
          setMode('verify');
          if (res?.guardian_consent?.needs_consent) {
            try { const { toast } = await import('sonner'); toast.success(`We also sent a consent email to ${res.guardian_consent.guardian_email}.`); } catch (_) { /* ignore */ }
          }
        } else if (res?.guardian_consent?.needs_consent) {
          setLoginOpen(false);
          try { const { toast } = await import('sonner'); toast.success(`We sent a consent email to ${res.guardian_consent.guardian_email}. Your account will unlock once your guardian approves.`); } catch (_) { /* ignore */ }
        } else {
          setLoginOpen(false);
        }
      } else if (mode === 'forgot') {
        // POST reset request. Backend returns a generic message no matter
        // what so we don't leak which emails exist.
        const backend = process.env.REACT_APP_BACKEND_URL;
        const r = await fetch(`${backend}/api/auth/password-reset/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email: form.email.trim().toLowerCase() }),
        });
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw { response: { data } };
        }
        setNotice("Check your inbox for a password reset link. It expires in 1 hour.");
        setMode('forgot_sent');
      } else {
        await emailLogin({ email: form.email.trim().toLowerCase(), password: form.password, remember: form.remember, refCode: form.refCode.trim().toUpperCase() || null });
        setLoginOpen(false);
      }
    } catch (err) {
      const raw = err?.response?.data?.detail;
      // Backend returns a structured `{code:'no_password_set', ...}` when
      // the email is registered via Google. Show the targeted CTA row
      // instead of the generic "invalid email or password" error.
      if (raw && typeof raw === 'object' && raw.code === 'no_password_set') {
        setNoPasswordMode(true);
        setError('');
      } else if (raw && typeof raw === 'object' && raw.code === 'email_not_verified') {
        // iter673 · Unverified account tried to sign in — send them to the
        // code step (backend already re-issued a fresh code).
        const em = raw.email || form.email.trim().toLowerCase();
        setPendingEmail(em);
        setError('');
        setNotice(`We sent a 6-digit code to ${em}. Enter it below to finish signing in.`);
        setCode('');
        setMode('verify');
      } else {
        const msg = (raw && typeof raw === 'string') ? raw : (raw?.message || 'Something went wrong. Try again.');
        setError(msg);
      }
    } finally { setBusy(false); }
  };
  // iter673 · Verify the 6-digit code → activates + logs in.
  const handleVerify = async () => {
    if (code.length !== 6) { setError('Enter the 6-digit code.'); return; }
    setError(''); setBusy(true);
    try {
      await verifyEmail({ email: pendingEmail || form.email, code });
      setLoginOpen(false);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setError((typeof raw === 'string' ? raw : raw?.message) || 'Verification failed. Try again.');
    } finally { setBusy(false); }
  };
  // iter673 · Resend a code from the verify step.
  const handleResend = async () => {
    setError(''); setNotice(''); setBusy(true);
    try {
      const em = pendingEmail || form.email.trim().toLowerCase();
      const r = await resendVerification(em);
      setNotice(r?.message || `A new code is on its way to ${em}.`);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setError((typeof raw === 'string' ? raw : raw?.message) || 'Could not resend just yet. Try again shortly.');
    } finally { setBusy(false); }
  };
  // iter673 · "Resend verification" from the login screen. Requires an
  // email; kicks the user into the verify step.
  const handleResendFromLogin = async () => {
    const em = form.email.trim().toLowerCase();
    if (!em || !em.includes('@')) { setError('Enter your email above first, then tap Resend.'); return; }
    setError(''); setNotice(''); setBusy(true);
    try {
      const r = await resendVerification(em);
      setPendingEmail(em);
      setNotice(r?.message || `If that account needs verification, a code is on its way to ${em}.`);
      setCode('');
      setMode('verify');
    } catch (err) {
      const raw = err?.response?.data?.detail;
      setError((typeof raw === 'string' ? raw : raw?.message) || 'Could not resend just yet. Try again shortly.');
    } finally { setBusy(false); }
  };
  return createPortal(
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-md p-4"
      onClick={() => setLoginOpen(false)}
      data-testid="login-modal-overlay"
    >
      <div
        className="relative w-full max-w-md bg-black text-white rounded-[28px] overflow-hidden ring-1 ring-white/10 shadow-2xl max-h-[90dvh] overflow-y-auto"
        style={{ animation: 'fadeSlideUpCentered 360ms cubic-bezier(0.22, 1, 0.36, 1)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="login-modal"
      >
        <button type="button" onClick={() => setLoginOpen(false)} aria-label="Close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center z-10"
          data-testid="login-modal-close">
          <X className="w-4 h-4" />
        </button>
        <div className="px-6 pt-8 pb-6">
          <p className="font-italic text-xs uppercase tracking-widest text-blue-300 mb-2">
            {mode === 'signup' ? 'Create Your FLYT ID'
              : mode === 'forgot' || mode === 'forgot_sent' ? 'Reset your password'
              : mode === 'verify' ? 'Verify your email'
              : 'Welcome back'}
          </p>
          <h2 className="font-display text-3xl uppercase leading-[1.05] mb-1">
            {mode === 'signup' ? 'Step inside.'
              : mode === 'forgot' ? 'Forgot it?'
              : mode === 'forgot_sent' ? 'Check your inbox.'
              : mode === 'verify' ? 'Check your inbox.'
              : 'Sign in.'}
          </h2>
          <p className="text-sm text-gray-400 mb-6">
            {mode === 'signup' ? 'Save journeys, follow athletes, build your FLYT ID.'
              : mode === 'forgot' ? 'Drop your email and we\'ll send a magic link to set a new password.'
              : mode === 'forgot_sent' ? notice
              : mode === 'verify' ? 'Enter the 6-digit code we emailed you to activate your account.'
              : 'Pick up where you left off.'}
          </p>
          {/* iter673 · verify — 6-digit code entry. No form, custom panel. */}
          {mode === 'verify' ? (
            <div className="space-y-3" data-testid="verify-panel">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="w-full text-center tracking-[0.6em] text-2xl font-bold text-white bg-white/5 ring-1 ring-white/15 focus:ring-blue-400 rounded-xl py-3 outline-none placeholder-white/25"
                data-testid="verify-code-input"
                autoFocus
              />
              {notice && <p className="text-xs text-gray-400" data-testid="verify-notice">{notice}</p>}
              {error && <p className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="verify-error">{error}</p>}
              <button
                type="button"
                onClick={handleVerify}
                disabled={busy || code.length !== 6}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-blue-500 text-white font-bold text-sm hover:bg-blue-600 transition disabled:opacity-50"
                data-testid="verify-submit-btn">
                {busy ? 'Verifying…' : 'Verify & continue'}
              </button>
              <div className="flex items-center justify-between text-xs pt-0.5">
                <button type="button" onClick={handleResend} disabled={busy} className="text-blue-300 hover:text-white disabled:opacity-50" data-testid="verify-resend-btn">
                  Resend code
                </button>
                <button type="button" onClick={() => { setMode('login'); setError(''); setNotice(''); setCode(''); }} className="text-white/60 hover:text-white" data-testid="verify-back-btn">
                  Use a different email
                </button>
              </div>
            </div>
          ) : mode === 'forgot_sent' ? (
            <div className="space-y-3">
              <button type="button" onClick={() => { setMode('login'); setError(''); setNotice(''); }}
                className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-blue-500 text-white font-bold text-sm hover:bg-blue-600 transition"
                data-testid="back-to-login-btn">
                Back to sign in
              </button>
            </div>
          ) : (
          <form onSubmit={submit} className="space-y-3" data-testid="email-auth-form">
            {mode === 'signup' && (
              <FieldRow icon={UserIcon} label="Full name" type="text" placeholder="Jordan Smith" value={form.name} onChange={onChange('name')} autoComplete="name" data-testid="login-name-input" />
            )}
            {/* iter863 · Jay: DOB is no longer asked here — moved to the
                universal post-login DobGuard so Google/Apple/email
                signups all go through the exact same one-time gate. */}
            {/* iter377 · Recent-emails MRU chips. Native iOS + web:
                tap a chip to fill the email input; iOS Passwords /
                Face ID autofill then offers the saved password. Only
                shown when the user is signing in (not on signup) so
                new users aren't confused by "old accounts". */}
            {mode === 'login' && recentEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pb-1" data-testid="login-recent-emails">
                <span className="text-[10px] uppercase tracking-[0.2em] text-white/45 self-center pr-1">Recent</span>
                {recentEmails.map((em) => (
                  <button
                    key={em}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, email: em }))}
                    className="px-3 py-1 rounded-full bg-white/[0.06] hover:bg-white/[0.12] ring-1 ring-white/10 text-[11px] text-white/85 transition active:scale-95"
                    data-testid={`login-recent-email-${em.replace(/[^a-z0-9]/gi, '-')}`}
                    title={`Sign in as ${em}`}
                  >
                    {em}
                  </button>
                ))}
              </div>
            )}
            {/* iter377 · `autoComplete="username"` (Apple's preferred
                token) triggers the iOS Passwords credential picker
                properly. Was previously `"email"` which some builds
                treat as a plain text field. */}
            <FieldRow icon={Mail} label="Email" type="email" placeholder="you@example.com" value={form.email} onChange={onChange('email')} autoComplete="username" required data-testid="login-email-input" />
            {mode !== 'forgot' && (
              <FieldRow icon={Lock} label="Password" type="password" placeholder={mode === 'signup' ? 'At least 8 characters' : 'Your password'} value={form.password} onChange={onChange('password')} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required data-testid="login-password-input" />
            )}
            {mode === 'signup' && (
              <FieldRow icon={Lock} label="Confirm password" type="password" placeholder="Re-enter password" value={form.confirm} onChange={onChange('confirm')} autoComplete="new-password" required data-testid="login-confirm-input" />
            )}
            {/* iter304 · Ambassador referral code — visible optional
                field on both signup + login. If a code is already
                stashed from a `?ref=` visit we pre-fill and show a
                "credit captured" hint. */}
            {mode !== 'forgot' && (
              <div>
                <FieldRow
                  icon={Sparkles}
                  label={mode === 'signup' ? 'Referral code (optional)' : 'Have an ambassador code?'}
                  type="text"
                  placeholder="e.g. UP4B4U"
                  value={form.refCode}
                  onChange={(e) => setForm({ ...form, refCode: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12) })}
                  autoComplete="off"
                  data-testid="login-refcode-input"
                />
                {form.refCode && form.refCode.length >= 4 && (
                  <p className="mt-1 text-[10px] text-yellow-300/80 flex items-center gap-1 pl-1" data-testid="login-refcode-hint">
                    <Sparkles className="w-3 h-3" />
                    Ambassador <span className="font-mono font-bold tracking-widest">{form.refCode}</span> will be credited when you {mode === 'signup' ? 'sign up' : 'sign in'}.
                  </p>
                )}
              </div>
            )}
            {mode === 'login' && (
              <div className="flex items-center justify-between pt-1 text-xs">
                <label className="inline-flex items-center gap-1.5 text-white/70 cursor-pointer">
                  <input type="checkbox" checked={form.remember} onChange={(e) => setForm({ ...form, remember: e.target.checked })} className="w-3.5 h-3.5 accent-blue-500" />
                  Keep me signed in
                </label>
                <button type="button" onClick={() => { setMode('forgot'); setError(''); setNoPasswordMode(false); }} className="text-blue-300 hover:text-white" data-testid="forgot-password-btn">
                  Forgot password?
                </button>
              </div>
            )}
            {/* iter673 · Resend verification for users who didn't get the code. */}
            {mode === 'login' && (
              <div className="text-xs pt-0.5" data-testid="login-resend-verification-row">
                <button type="button" onClick={handleResendFromLogin} disabled={busy} className="text-white hover:text-white/80 disabled:opacity-50" data-testid="login-resend-verification-btn">
                  Didn't get your verification code? Resend
                </button>
              </div>
            )}
            {noPasswordMode && (
              <div className="text-xs bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 space-y-2" data-testid="no-password-prompt">
                <p className="text-blue-100">
                  This email is registered with Google. Continue with Google, or send yourself a password-reset link to enable email login.
                </p>
                <div className="flex gap-2">
                  <button type="button" onClick={() => runProviderLogin(() => startGoogleLogin('/profile'), setError)}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full bg-white text-black text-xs font-bold hover:bg-gray-100 transition"
                    data-testid="no-password-google-btn">
                    <GoogleGlyph /> Continue with Google
                  </button>
                  <button type="button" onClick={() => { setMode('forgot'); setError(''); setNoPasswordMode(false); }}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-full ring-1 ring-white/20 text-xs font-bold text-white hover:bg-white/10 transition"
                    data-testid="no-password-reset-btn">
                    Set a password
                  </button>
                </div>
              </div>
            )}
            {error && (
              <p className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">{error}</p>
            )}
            {/* iter447 · Apple Guideline 1.2 · EULA acceptance BEFORE
                account creation. Zero-tolerance for objectionable
                content wording is required — links to our Terms and
                Apple's standard EULA. */}
            {mode === 'signup' && (
              <label className="flex items-start gap-3 rounded-xl bg-white/[0.04] ring-1 ring-white/10 px-3 py-2.5 cursor-pointer" data-testid="signup-eula-row">
                <input
                  type="checkbox"
                  checked={agreedEula}
                  onChange={(e) => setAgreedEula(e.target.checked)}
                  className="mt-0.5 w-4 h-4 shrink-0 rounded"
                  data-testid="signup-eula-checkbox"
                />
                <span className="text-[11px] text-white/70 leading-snug">
                  I agree to Flythouse&apos;s{' '}
                  <a href="/terms" target="_blank" rel="noreferrer" className="underline text-white hover:text-blue-300" data-testid="signup-eula-terms-link">Terms of Service</a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="underline text-white hover:text-blue-300" data-testid="signup-eula-privacy-link">Privacy Policy</a>.
                  {' '}I confirm I own — or have the necessary rights and permission for — any content I upload to Flythouse, and I understand Flythouse has <span className="font-bold text-white">zero tolerance</span> for objectionable content or abusive users, with reported content reviewed within 24 hours.
                </span>
              </label>
            )}
            {/* iter754 · `disabled` no longer includes the EULA check —
                a disabled button silently eats the click, which is
                exactly the "why won't this do anything" confusion Jay
                flagged. The button still LOOKS grayed out (opacity-60
                class covers all four conditions) but a tap while only
                the EULA box is unchecked now reaches submit()'s own
                check above and gets a real toast telling them why. */}
            <button type="submit" disabled={busy}
              className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold text-sm transition active:scale-[0.99] bg-white text-black hover:bg-gray-100 ${(busy || (mode === 'signup' && !agreedEula)) ? 'opacity-60' : ''}`}
              data-testid="login-submit-btn">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />}
              {mode === 'signup' ? 'Create FLYT ID'
                : mode === 'forgot' ? 'Send reset link'
                : 'Sign in with Email'}
            </button>
            {mode === 'forgot' && (
              <button type="button" onClick={() => { setMode('login'); setError(''); }}
                className="w-full text-center text-xs text-white/60 hover:text-white pt-2"
                data-testid="cancel-forgot-btn">
                ← Back to sign in
              </button>
            )}
          </form>
          )}
          {/* Social options. Both Apple and Google work everywhere:
              Apple uses the AppleID JS SDK on web + the native
              AuthenticationServices sheet in the iOS shell; Google
              runs in the browser and — on iOS — opens the OAuth URL
              in SFSafariViewController so Google actually completes
              (see lib/nativeBridge.js `openExternalAuth`). Universal
              Links carry the callback back into the app. */}
          {mode !== 'forgot' && mode !== 'forgot_sent' && (
            <>
              <div className="flex items-center gap-3 my-4 text-[10px] uppercase tracking-widest text-white/40">
                <div className="flex-1 h-px bg-white/15" /> or <div className="flex-1 h-px bg-white/15" />
              </div>
              <button type="button"
                onClick={() => {
                  // iter447 · Apple Guideline 1.2 — EULA acceptance must
                  // gate account creation regardless of WHICH button
                  // creates the account. The email path already checked
                  // `agreedEula`; Google/Apple were bypassing it entirely
                  // (same account-creation action, different button).
                  // iter754 · This check used to be dead code in practice
                  // — the button was ALSO natively `disabled` for the
                  // exact same condition, and a disabled button never
                  // fires onClick at all, so tapping it while greyed out
                  // did nothing with zero explanation (Jay's report).
                  // Native `disabled` below no longer covers this case —
                  // the tap now actually reaches this check and shows a
                  // real toast instead of silently doing nothing.
                  if (mode === 'signup' && !agreedEula) {
                    toast.error("Please agree to Flythouse's Terms of Service to continue.");
                    setError('Please agree to Flythouse\'s Terms of Service to continue.');
                    return;
                  }
                  runProviderLogin(() => startGoogleLogin('/profile'), setError);
                }}
                className={`w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-full bg-white text-black font-bold text-sm hover:bg-gray-100 transition active:scale-[0.99] mb-2 ${mode === 'signup' && !agreedEula ? 'opacity-50' : ''}`}
                data-testid="login-google-btn">
                <GoogleGlyph /> Continue with Google
              </button>
              {/* Sign in with Apple — web: AppleID JS SDK; native: the
                  AuthenticationServices sheet via Capacitor plugin. */}
              {appleAvailable && (
              <button
                type="button"
                onClick={() => {
                  // iter754 · Same fix as the Google button above.
                  if (mode === 'signup' && !agreedEula) {
                    toast.error("Please agree to Flythouse's Terms of Service to continue.");
                    setError('Please agree to Flythouse\'s Terms of Service to continue.');
                    return;
                  }
                  runProviderLogin(() => startAppleLogin && startAppleLogin('/profile'), setError);
                }}
                className={`w-full inline-flex items-center justify-center gap-3 px-5 py-3 rounded-full bg-black text-white font-bold text-sm ring-1 ring-white/20 hover:ring-white/40 transition active:scale-[0.99] ${mode === 'signup' && !agreedEula ? 'opacity-50' : ''}`}
                data-testid="login-apple-btn"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M17.05 12.04a4.69 4.69 0 0 1 2.24-3.93 4.83 4.83 0 0 0-3.81-2.06c-1.6-.17-3.14.94-3.96.94-.83 0-2.09-.92-3.43-.9A5.06 5.06 0 0 0 3.81 8.7c-1.84 3.18-.47 7.87 1.31 10.45.87 1.27 1.9 2.69 3.26 2.64 1.31-.05 1.8-.84 3.39-.84 1.58 0 2.03.84 3.42.81 1.41-.02 2.31-1.29 3.18-2.57.99-1.49 1.4-2.93 1.43-3.01-.03-.01-2.72-1.04-2.75-4.14ZM14.38 4.32a4.62 4.62 0 0 0 1.06-3.32 4.79 4.79 0 0 0-3.1 1.61 4.39 4.39 0 0 0-1.09 3.22 3.95 3.95 0 0 0 3.13-1.51Z"/>
                </svg>
                Sign in with Apple
              </button>
              )}
            </>
          )}
          {mode !== 'forgot' && mode !== 'forgot_sent' && (
            <p className="mt-4 text-[12px] text-center text-white/60">
              {mode === 'signup' ? (
                <>Already have an account? <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-blue-300 hover:text-white font-bold" data-testid="switch-to-login">Sign in</button></>
              ) : (
                <>New here? <button type="button" onClick={() => { setMode('signup'); setError(''); }} className="text-blue-300 hover:text-white font-bold" data-testid="switch-to-signup">Create account</button></>
              )}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};
export default LoginModal;
